import React, { useMemo } from "react";
import { PlusIcon, MinusCircleIcon } from "@heroicons/react/20/solid";
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import TimeSelector from "../Shared/TimeSelector";
import { DateTime } from "luxon";
import { nanoid } from "nanoid";
import ErrorMessage from "../Shared/ErrorMessage";

const WeeklyAvailability = ({ availability, setAvailability }) => {
  const maxTime = "23:59";

  const handleAddBlock = (index) => {
    const updatedAvailability = [...availability];
    if (updatedAvailability[index].unavailable) {
      return;
    }

    const prevBlockIndex = availability[index].blocks.length - 1;
    let prevBlockEndTime =
      availability[index].blocks[prevBlockIndex].endTime.name;

    if (prevBlockEndTime === maxTime) return;

    const [hour, minute] = prevBlockEndTime.split(":").map(Number);
    prevBlockEndTime = DateTime.now().set({ hour, minute, second: 0 });

    let defaultStartTime = prevBlockEndTime.plus({ hours: 1 });
    let defaultEndTime = defaultStartTime.plus({ hours: 1 });

    const isSameDay =
      prevBlockEndTime.hasSame(defaultStartTime, "day") &&
      prevBlockEndTime.hasSame(defaultEndTime, "day");

    if (isSameDay) {
      defaultStartTime = defaultStartTime.toFormat("HH:mm");
      defaultEndTime = defaultEndTime.toFormat("HH:mm");
    } else {
      defaultStartTime = prevBlockEndTime.plus({ minutes: 1 }).toFormat("HH:mm");
      defaultEndTime = maxTime;
    }

    updatedAvailability[index].blocks.push({
      startTime: {
        id: nanoid(),
        name: defaultStartTime,
      },
      endTime: {
        id: nanoid(),
        name: defaultEndTime,
      },
    });

    setAvailability(updatedAvailability);
  };

  const handleRemoveBlockOrMarkUnavailable = (index) => {
    const updatedAvailability = [...availability];
    if (updatedAvailability[index].blocks.length > 1) {
      updatedAvailability[index].blocks.pop(); // Remove the most recent block
    } else {
      updatedAvailability[index].unavailable = true; // Mark the day as unavailable
      updatedAvailability[index].blocks = []; // Clear all blocks
    }
    setAvailability(updatedAvailability);
  };

  const handleRemoveBlockAdditionalTimeSelectors = (index, blockIndex) => {
    const updatedAvailability = [...availability];
    if (
      updatedAvailability[index] &&
      updatedAvailability[index].blocks[blockIndex] !== undefined // Ensure the dayIndex and blockIndex are valid
    ) {
      updatedAvailability[index].blocks.splice(blockIndex, 1); // Remove the block at blockIndex
    }
    setAvailability(updatedAvailability);
  };

  const handleBlockChange = (index, blockIndex, field, value) => {
    const updatedAvailability = [...availability];
    updatedAvailability[index].blocks[blockIndex][field] = value;
    setAvailability(updatedAvailability);
  };

  const toggleUnavailable = (index) => {
    const updatedAvailability = [...availability];
    updatedAvailability[index].unavailable =
      !updatedAvailability[index].unavailable;

    if (updatedAvailability[index].unavailable) {
      updatedAvailability[index].blocks = [];
    } else {
      updatedAvailability[index].blocks.push({
        startTime: { id: nanoid(), name: "09:00" },
        endTime: { id: nanoid(), name: "17:00" },
      });
    }

    setAvailability(updatedAvailability);
  };

  const isAllowedToAddBlocks = (index) => {
    for (const block of availability[index].blocks) {
      if (block.endTime.name === maxTime) {
        return false;
      }
    }

    const hasError = errors.some((error) => error.index === index);
    return !hasError;
  };

  const handleDuplicateAvailability = (index) => {
    const updatedAvailability = [...availability];

    // Get the selected day's availability blocks
    const selectedDayBlocks = updatedAvailability[index].blocks.map(
      (block) => ({
        startTime: { id: nanoid(), name: block.startTime.name },
        endTime: { id: nanoid(), name: block.endTime.name },
      })
    );

    // Loop through all days and duplicate the selected day's availability
    updatedAvailability.forEach((day, availabilityIndex) => {
      if (availabilityIndex !== index && !day.unavailable) {
        updatedAvailability[availabilityIndex].blocks = selectedDayBlocks.map(
          (block) => ({
            startTime: { id: nanoid(), name: block.startTime.name },
            endTime: { id: nanoid(), name: block.endTime.name },
          })
        );
      }
    });

    setAvailability(updatedAvailability);
  };

  const errors = useMemo(() => {
    const errorBlocks = [];
    availability.forEach((day, index) => {
      day.blocks.forEach((block, blockIndex) => {
        let isEndBeforeStart;
        let isPeriodOverlap;

        const startTime = DateTime.fromISO(`1970-01-01T${block.startTime.name}`);
        const endTime = DateTime.fromISO(`1970-01-01T${block.endTime.name}`);

        // validate end times are not before start times
        if (block.endTime.name === maxTime) {
          isEndBeforeStart = false;
        } else if (endTime < startTime || startTime.equals(endTime)) {
          isEndBeforeStart = true;
        } else {
          isEndBeforeStart = false;
        }

        // check for period overlaps
        if (blockIndex === 0) {
          isPeriodOverlap = false;
        } else {
          let prevBlockEndTime =
            availability[index].blocks[blockIndex - 1].endTime.name;
          prevBlockEndTime = DateTime.fromISO(`1970-01-01T${prevBlockEndTime}`);
          isPeriodOverlap = startTime < prevBlockEndTime;
        }

        if (isEndBeforeStart) {
          errorBlocks.push({
            index: index,
            blockIndex: blockIndex,
            errorMessage: "Start time must be before end time.",
            time: block.endTime,
            isStartError: true,
            isEndError: false,
          });
        } else if (isPeriodOverlap) {
          errorBlocks.push({
            index: index,
            blockIndex: blockIndex,
            errorMessage: "Periods cannot overlap.",
            time: block.startTime,
            isStartError: true,
            isEndError: false,
          });
        }
      });
    });
    return errorBlocks;
  }, [availability, maxTime]);

  // Memoized error lookup utilities to avoid repeated finds
  const getBlockError = useMemo(() => {
    const errorMap = new Map();
    errors.forEach(error => {
      const key = `${error.index}-${error.blockIndex}`;
      errorMap.set(key, error);
    });
    return (index, blockIndex) => errorMap.get(`${index}-${blockIndex}`);
  }, [errors]);

  const hasBlockError = useMemo(() => {
    const errorSet = new Set();
    errors.forEach(error => {
      errorSet.add(`${error.index}-${error.blockIndex}`);
    });
    return (index, blockIndex) => errorSet.has(`${index}-${blockIndex}`);
  }, [errors]);

  return (
    <div>
      <h2 className="block text-base font-medium leading-6 text-ink-900 mb-3">
        Weekly Hours
      </h2>
      {availability.map((day, index) => (
        <div key={day.day} className="space-y-2 pb-2">
          {/* Main row for the day and first block */}
          <div className="flex items-center justify-between gap-x-2">
            {/* Day of the week (always visible for the first block) */}
            <div className="w-[60px]">
              <h3
                className={`text-sm font-medium text-ink-800 pt-2 ${
                  day.unavailable ? "line-through text-ink-400 pt-2" : ""
                }`}
              >
                {`${day.day[0].toUpperCase()}${day.day.slice(1, 3)}`}
              </h3>
            </div>

            {/* First TimeSelector block (compact) */}
            {!day.unavailable && day.blocks.length > 0 && (
              <div className="flex-grow max-w-sm">
                <div className="flex gap-x-2">
                  {/* Start Time*/}
                  <TimeSelector
                    key={availability[index].blocks[0].startTime.id}
                    onSelect={(value) =>
                      handleBlockChange(index, 0, "startTime", value)
                    }
                    value={availability[index].blocks[0].startTime}
                    isError={getBlockError(index, 0)?.isStartError}
                  />
                  {/* End Time */}
                  <TimeSelector
                    key={availability[index].blocks[0].endTime.id}
                    onSelect={(value) =>
                      handleBlockChange(index, 0, "endTime", value)
                    }
                    value={availability[index].blocks[0].endTime}
                    isError={getBlockError(index, 0)?.isEndError}
                  />
                </div>
                {hasBlockError(index, 0) ? (
                  <ErrorMessage
                    message={getBlockError(index, 0)?.errorMessage}
                  />
                ) : null}
              </div>
            )}

            {/* Icons */}
            <div className="flex items-center gap-x-1 pt-1">
              {day.unavailable ? (
                <span className="text-sm font-medium text-ink-800">
                  Unavailable
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleRemoveBlockOrMarkUnavailable(index)}
                  className="p-0.5 rounded hover:bg-cream-200 text-accent-600"
                  title="Remove"
                >
                  <MinusCircleIcon className="h-5 w-5" />
                  <span className="sr-only">Remove</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (day.unavailable) {
                    toggleUnavailable(index);
                  } else {
                    handleAddBlock(index);
                  }
                }}
                className={`p-0.5 rounded hover:bg-cream-200 ${
                  isAllowedToAddBlocks(index)
                    ? "text-accent-600"
                    : "text-accent-400 opacity-50 cursor-not-allowed"
                }`}
                title={
                  day.unavailable ? "Make available" : "Add time block"
                }
                disabled={!day.unavailable && !isAllowedToAddBlocks(index)}
              >
                <PlusIcon className="h-5 w-5" />
                <span className="sr-only">
                  {day.unavailable ? "Make available" : "Add time block"}
                </span>
              </button>
              {day.unavailable ? null : (
                <button
                  type="button"
                  onClick={() => handleDuplicateAvailability(index)}
                  className="p-0.5 rounded hover:bg-cream-200 text-accent-600"
                  title="Apply to all days"
                >
                  <DocumentDuplicateIcon className="h-5 w-5" />
                  <span className="sr-only">Apply to all days</span>
                </button>
              )}
            </div>
          </div>

          {/* Additional TimeSelector blocks */}
          {!day.unavailable && day.blocks.length > 1 && (
            <div className="space-y-2">
              {day.blocks.slice(1).map((block, blockIndex) => (
                <div
                  key={`${block.startTime.id}-${block.endTime.id}`}
                  className="flex items-center justify-between gap-x-2"
                >
                  {/* Invisible Day for subsequent blocks - layout */}
                  <h3 className="text-sm font-medium text-ink-800 invisible w-[60px]">
                    {day.day.substring(0, 3)}
                  </h3>

                  <div className="flex-grow max-w-sm">
                    <div className="flex gap-x-2">
                      {/* Start Time*/}
                      <TimeSelector
                        key={block.startTime.id}
                        onSelect={(value) => {
                          handleBlockChange(
                            index,
                            blockIndex + 1,
                            "startTime",
                            value
                          );
                        }}
                        value={
                          availability[index].blocks[blockIndex + 1].startTime
                        }
                        timeDataFrom={
                          availability[index].blocks[blockIndex].endTime.name
                        }
                        isError={getBlockError(index, blockIndex + 1)?.isStartError}
                      />
                      {/* End Time */}
                      <TimeSelector
                        key={block.endTime.id}
                        onSelect={(value) =>
                          handleBlockChange(
                            index,
                            blockIndex + 1,
                            "endTime",
                            value
                          )
                        }
                        value={
                          availability[index].blocks[blockIndex + 1].endTime
                        }
                        timeDataFrom={
                          availability[index].blocks[blockIndex + 1].startTime
                            .name
                        }
                        isError={getBlockError(index, blockIndex + 1)?.isEndError}
                      />
                    </div>
                    {hasBlockError(index, blockIndex + 1) ? (
                      <ErrorMessage
                        message={getBlockError(index, blockIndex + 1)?.errorMessage}
                      />
                    ) : null}
                  </div>
                  <div className="flex items-center gap-x-1 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        handleRemoveBlockAdditionalTimeSelectors(
                          index,
                          blockIndex + 1
                        );
                      }}
                      className="p-0.5 rounded hover:bg-cream-200 text-accent-600"
                      title="Remove"
                    >
                      <MinusCircleIcon className="h-5 w-5" />
                      <span className="sr-only">Remove</span>
                    </button>
                    {/* Invisible button spacers mirroring the primary row's Add and Duplicate buttons */}
                    <button
                      type="button"
                      aria-hidden="true"
                      tabIndex={-1}
                      className="p-0.5 rounded text-accent-600 invisible"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      aria-hidden="true"
                      tabIndex={-1}
                      className="p-0.5 rounded text-accent-600 invisible"
                    >
                      <DocumentDuplicateIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default React.memo(WeeklyAvailability);
